// Site-wide Curtain Ledger effects, rendered once by the layout:
// boiling hand-drawn lines, squishy presses, the star cursor, the red mouth
// that gulps between pages, the star that flies into the cart, and opt-in sound.
const reduceMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
const SOUND_KEY = 'cl-sound';
const GULP_KEY = 'cl-gulp';

class ClFx extends HTMLElement {
  #ac;
  #audio = null;
  #soundOn = false;
  #parts = [];
  #running = false;
  #ctx = null;

  connectedCallback() {
    this.#ac = new AbortController();
    const signal = this.#ac.signal;
    try { this.#soundOn = localStorage.getItem(SOUND_KEY) === 'on'; } catch (e) { this.#soundOn = false; }
    this.#syncSoundButtons();

    document.addEventListener('click', (e) => this.#onClick(e), { signal });
    document.addEventListener('pointerdown', (e) => this.#onPress(e), { signal, passive: true });
    document.addEventListener('submit', (e) => this.#onSubmit(e), { signal, capture: true });
    document.addEventListener('cl:sound', (e) => this.#sfx(e.detail), { signal });
    addEventListener('pageshow', (e) => { if (e.persisted) this.#hideGulp(); }, { signal });

    this.#arrive();
    if (reduceMotion()) return;
    this.#boil(signal);
    this.#cursor(signal);
    this.#scribbles(signal);
  }

  // Red underlines draw themselves in when their heading scrolls into view.
  #scribbles(signal) {
    if (!('IntersectionObserver' in window)) return;
    const io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (!en.isIntersecting) continue;
        en.target.classList.remove('cl-undrawn');
        io.unobserve(en.target);
      }
    }, { threshold: 0.6 });
    for (const s of document.querySelectorAll('.cl-scribble')) {
      if (s.getBoundingClientRect().top > innerHeight * 0.8) { s.classList.add('cl-undrawn'); io.observe(s); }
    }
    signal.addEventListener('abort', () => io.disconnect());
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  // Swap the turbulence seed so every hand-drawn line jitters like redrawn cartoon frames.
  #boil(signal) {
    const turb = document.getElementById('cl-boil-turb');
    if (!turb) return;
    const seeds = [2, 5, 9];
    let i = 0;
    const id = setInterval(() => {
      if (document.hidden) return;
      i = (i + 1) % seeds.length;
      turb.setAttribute('seed', String(seeds[i]));
    }, 130);
    signal.addEventListener('abort', () => clearInterval(id));
  }

  #onPress(e) {
    const el = e.target.closest?.('.button-primary, .button-outline, .button-secondary, .cl-squish');
    if (!el) return;
    el.classList.remove('cl-squishing');
    void el.offsetWidth;
    el.classList.add('cl-squishing');
    setTimeout(() => el.classList.remove('cl-squishing'), 420);
    if (!el.matches('[data-cl-sound-toggle]')) this.#sfx('squeak');
  }

  #onClick(e) {
    const toggle = e.target.closest?.('[data-cl-sound-toggle]');
    if (toggle) {
      this.#soundOn = !this.#soundOn;
      try { localStorage.setItem(SOUND_KEY, this.#soundOn ? 'on' : 'off'); } catch (err) { /* private mode */ }
      this.#syncSoundButtons();
      if (this.#soundOn) this.#sfx('squeak');
      return;
    }

    const a = e.target.closest?.('a[href]');
    if (!a || e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    if (a.target && a.target !== '_self') return;
    if (a.hasAttribute('download') || a.closest('[data-cl-no-gulp]')) return;
    const url = new URL(a.href, location.href);
    if (url.origin !== location.origin) return;
    if (url.pathname === location.pathname && url.search === location.search && url.hash) return;
    if (reduceMotion() || !Element.prototype.animate) return;

    e.preventDefault();
    this.#gulp(e.clientX || innerWidth / 2, e.clientY || innerHeight / 2, () => {
      try { sessionStorage.setItem(GULP_KEY, '1'); } catch (err) { /* private mode */ }
      location.href = url.href;
    });
  }

  #gulp(x, y, done) {
    const mouth = document.getElementById('cl-gulp');
    if (!mouth) { done(); return; }
    const w = 180;
    mouth.style.left = `${x - w / 2}px`;
    mouth.style.top = `${y - w * 0.3}px`;
    mouth.hidden = false;
    const big = (Math.hypot(innerWidth, innerHeight) / w) * 2.8;
    const anim = mouth.animate(
      [{ transform: 'scale(.05) rotate(-25deg)' }, { transform: `scale(${big}) rotate(8deg)` }],
      { duration: 420, easing: 'cubic-bezier(.6,0,.9,.45)', fill: 'forwards' }
    );
    this.#sfx('gulp');
    anim.onfinish = done;
  }

  // The previous page set a flag before it navigated; the head bootstrap already painted
  // the screen red, so the mouth now spits the new page out.
  #arrive() {
    if (!document.documentElement.classList.contains('cl-arriving')) return;
    const cover = document.getElementById('cl-arrive');
    if (!cover || reduceMotion() || !Element.prototype.animate) { this.#hideGulp(); return; }
    const anim = cover.animate(
      [{ clipPath: 'circle(150% at 50% 0%)' }, { clipPath: 'circle(0% at 50% 0%)' }],
      { duration: 520, easing: 'cubic-bezier(0.32, 0.72, 0, 1)', fill: 'forwards' }
    );
    anim.onfinish = () => this.#hideGulp();
  }

  #hideGulp() {
    document.documentElement.classList.remove('cl-arriving');
    const mouth = document.getElementById('cl-gulp');
    if (mouth) { mouth.hidden = true; mouth.getAnimations().forEach((a) => a.cancel()); }
  }

  // Product add-to-cart: shoot a star from the button into the header cart.
  #onSubmit(e) {
    const form = e.target;
    if (!(form instanceof HTMLFormElement) || !form.closest('s-product-form')) return;
    const btn = e.submitter;
    if (!btn || !btn.matches('[name="add"]')) return;
    const cart = document.getElementById('header-cart-trigger');
    this.#sfx('ding');
    if (reduceMotion() || !cart || !Element.prototype.animate) {
      document.dispatchEvent(new CustomEvent('cl:added'));
      return;
    }
    const r = btn.getBoundingClientRect();
    const c = cart.getBoundingClientRect();
    const star = document.createElement('span');
    star.className = 'cl-star-mask cl-flystar';
    star.setAttribute('aria-hidden', 'true');
    document.body.append(star);
    const x0 = r.left + r.width / 2; const y0 = r.top + r.height / 2;
    const x1 = c.left + c.width / 2; const y1 = c.top + c.height / 2;
    const mx = (x0 + x1) / 2; const my = Math.max(20, Math.min(y0, y1) - 170);
    const anim = star.animate([
      { transform: `translate(${x0 - 18}px, ${y0 - 18}px) scale(.6) rotate(0deg)` },
      { transform: `translate(${mx - 18}px, ${my - 18}px) scale(1.6) rotate(220deg)`, offset: 0.55 },
      { transform: `translate(${x1 - 18}px, ${y1 - 18}px) scale(.35) rotate(420deg)` },
    ], { duration: 720, easing: 'ease-in-out' });
    anim.onfinish = () => {
      star.remove();
      cart.classList.remove('cl-bump'); void cart.offsetWidth; cart.classList.add('cl-bump');
      document.dispatchEvent(new CustomEvent('cl:added'));
    };
  }

  // Desktop only: a star cursor that leaves a fading red sparkle trail.
  #cursor(signal) {
    if (!matchMedia('(pointer: fine)').matches) return;
    const cur = document.getElementById('cl-cursor');
    const cv = document.getElementById('cl-trail');
    if (!cur || !cv) return;
    this.#ctx = cv.getContext('2d');
    const size = () => {
      const dpr = Math.min(2, devicePixelRatio || 1);
      cv.width = innerWidth * dpr; cv.height = innerHeight * dpr;
      this.#ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    };
    size();
    addEventListener('resize', size, { signal });
    let started = false;
    addEventListener('pointermove', (e) => {
      if (e.pointerType !== 'mouse') return;
      if (!started) { started = true; document.documentElement.classList.add('cl-starcursor'); }
      cur.style.transform = `translate(${e.clientX}px, ${e.clientY}px)`;
      cur.classList.toggle('cl-big', !!e.target.closest?.('a, button, [role="button"], label, select, summary'));
      if (Math.random() < 0.55) {
        this.#parts.push({ x: e.clientX, y: e.clientY, vx: (Math.random() - 0.5) * 0.9, vy: Math.random() * 0.9 + 0.2, life: 1, s: 4 + Math.random() * 5, r: Math.random() * Math.PI });
      }
      if (!this.#running) { this.#running = true; requestAnimationFrame(() => this.#frame()); }
    }, { signal, passive: true });
    document.addEventListener('pointerleave', () => { document.documentElement.classList.remove('cl-starcursor'); started = false; }, { signal });
  }

  #frame() {
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, innerWidth, innerHeight);
    this.#parts = this.#parts.filter((p) => (p.life -= 0.028) > 0);
    for (const p of this.#parts) {
      p.x += p.vx; p.y += p.vy;
      const s = p.s * p.life;
      ctx.save(); ctx.translate(p.x, p.y); ctx.rotate(p.r);
      ctx.beginPath(); ctx.moveTo(0, -s);
      ctx.quadraticCurveTo(0, 0, s, 0); ctx.quadraticCurveTo(0, 0, 0, s);
      ctx.quadraticCurveTo(0, 0, -s, 0); ctx.quadraticCurveTo(0, 0, 0, -s);
      ctx.fillStyle = `rgba(225,37,27,${p.life.toFixed(2)})`; ctx.fill(); ctx.restore();
    }
    if (this.#parts.length) requestAnimationFrame(() => this.#frame());
    else { this.#running = false; ctx.clearRect(0, 0, innerWidth, innerHeight); }
  }

  #syncSoundButtons() {
    for (const b of document.querySelectorAll('[data-cl-sound-toggle]')) {
      b.setAttribute('aria-pressed', String(this.#soundOn));
      const label = b.querySelector('[data-cl-sound-label]');
      if (label) label.textContent = this.#soundOn ? b.dataset.onLabel : b.dataset.offLabel;
    }
  }

  // Tiny synthesized sounds, only after the visitor turns sound on.
  #sfx(kind) {
    if (!this.#soundOn) return;
    try {
      this.#audio ??= new (window.AudioContext || window.webkitAudioContext)();
      const c = this.#audio;
      if (c.state === 'suspended') c.resume();
      const tone = (type, f0, f1, dur, vol, delay = 0) => {
        const t = c.currentTime + delay; const o = c.createOscillator(); const g = c.createGain();
        o.type = type; o.frequency.setValueAtTime(f0, t); o.frequency.exponentialRampToValueAtTime(f1, t + dur);
        g.gain.setValueAtTime(vol, t); g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
        o.connect(g).connect(c.destination); o.start(t); o.stop(t + dur + 0.02);
      };
      if (kind === 'squeak') tone('triangle', 700, 1500, 0.09, 0.12);
      if (kind === 'ding') { tone('sine', 1320, 1320, 0.5, 0.16); tone('sine', 1980, 1980, 0.45, 0.1, 0.09); }
      if (kind === 'gulp') tone('sine', 420, 90, 0.32, 0.18);
      if (kind === 'bleh') {
        const t = c.currentTime; const o = c.createOscillator(); const ws = c.createWaveShaper(); const g = c.createGain();
        const lfo = c.createOscillator(); const lg = c.createGain();
        const curve = new Float32Array(1024);
        for (let i = 0; i < 1024; i += 1) { const x = (i * 2) / 1024 - 1; curve[i] = Math.tanh(x * 6); }
        ws.curve = curve; o.type = 'sawtooth'; lfo.frequency.value = 7; lg.gain.value = 22; lfo.connect(lg).connect(o.frequency);
        o.frequency.setValueAtTime(196, t); o.frequency.exponentialRampToValueAtTime(880, t + 0.35); o.frequency.exponentialRampToValueAtTime(660, t + 0.9);
        g.gain.setValueAtTime(0.0001, t); g.gain.exponentialRampToValueAtTime(0.12, t + 0.03); g.gain.exponentialRampToValueAtTime(0.0001, t + 1.1);
        o.connect(ws).connect(g).connect(c.destination); o.start(t); lfo.start(t); o.stop(t + 1.15); lfo.stop(t + 1.15);
      }
    } catch (err) { /* audio unavailable */ }
  }
}

if (!customElements.get('cl-fx')) customElements.define('cl-fx', ClFx);